// src/components/TrayPopover.jsx
// Compact popover that appears when the user clicks the Netwoke menu bar icon.
// Dimensions: 340×480 — frameless, no traffic lights.

import { useEffect, useState, useCallback } from 'react'
import { countryFlag, formatDuration } from '../utils/format'
import { useDataStore } from '../stores/dataStore'

const api = window.electronAPI

// ─── Tiny icon helper ─────────────────────────────────────────────────────────
const Ic = ({ size = 14, color = 'currentColor', sw = 1.8, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0 }}>
    {children}
  </svg>
)

const LockIcon   = ({ open }) => open
  ? <Ic size={11} color="#f5a623" sw={2}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></Ic>
  : <Ic size={11} color="#3ecf8e" sw={2}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></Ic>

const WarnIcon   = () => <Ic size={11} color="#f5a623" sw={2}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Ic>

// ─── Mini bar chart ───────────────────────────────────────────────────────────
function MiniChart({ history, dataKey, color }) {
  const vals = history.map(h => h[dataKey] || 0)
  const max  = Math.max(...vals, 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 24 }}>
      {vals.slice(-32).map((v, i) => (
        <div key={i} style={{
          flex: 1, minHeight: 1,
          borderRadius: '1px 1px 0 0',
          height: `${(v / max) * 100}%`,
          background: color,
          opacity: 0.2 + (v / max) * 0.75,
          transition: 'height 0.3s ease',
        }} />
      ))}
    </div>
  )
}

// ─── Connection row ───────────────────────────────────────────────────────────
function ConnRow({ conn }) {
  const warn = conn.riskLevel === 'warning' || conn.riskLevel === 'critical'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 14px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      background: warn ? 'rgba(245,166,35,0.03)' : 'transparent',
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: 6, flexShrink: 0,
        background: conn.isEncrypted ? 'rgba(62,207,142,0.1)' : 'rgba(245,166,35,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <LockIcon open={!conn.isEncrypted} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 500, color: '#dce8f5',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {conn.appName}
          <span style={{ color: 'rgba(220,232,245,0.35)', fontWeight: 400 }}> → </span>
          {conn.domain || conn.remoteIP}
        </div>
        <div style={{ fontSize: 9, color: 'rgba(220,232,245,0.35)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
          {countryFlag(conn.countryCode)} {conn.country} · {formatDuration(conn.durationMs)}
        </div>
      </div>
      {warn && (
        <div style={{ flexShrink: 0 }}>
          <WarnIcon />
        </div>
      )}
    </div>
  )
}

// ─── Alert row ────────────────────────────────────────────────────────────────
function AlertRow({ alert, onDismiss }) {
  return (
    <div style={{
      display: 'flex', gap: 8, padding: '8px 14px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      alignItems: 'flex-start',
    }}>
      <WarnIcon />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#f5a623', marginBottom: 2 }}>
          {alert.appName}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(220,232,245,0.5)', lineHeight: 1.45 }}>
          {alert.message}
        </div>
      </div>
      <button onClick={() => onDismiss(alert.id)} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'rgba(220,232,245,0.3)', fontSize: 14, flexShrink: 0, padding: 0,
        lineHeight: 1,
      }}>×</button>
    </div>
  )
}

// ─── Tab button ───────────────────────────────────────────────────────────────
function Tab({ label, active, count, warn, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '8px 0',
      background: 'none', border: 'none', cursor: 'pointer',
      borderBottom: `2px solid ${active ? '#4d9de0' : 'transparent'}`,
      color: active ? '#dce8f5' : 'rgba(220,232,245,0.4)',
      fontSize: 11, fontWeight: 500,
      fontFamily: "'IBM Plex Sans', sans-serif",
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      transition: 'color 0.15s',
    }}>
      {label}
      {count > 0 && (
        <span style={{
          fontSize: 9, padding: '1px 5px', borderRadius: 4,
          background: warn ? 'rgba(245,166,35,0.2)' : 'rgba(255,255,255,0.07)',
          color: warn ? '#f5a623' : 'rgba(220,232,245,0.4)',
          fontFamily: "'IBM Plex Mono', monospace",
        }}>{count}</span>
      )}
    </button>
  )
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function TrayPopover() {
  const [speed,   setSpeed]   = useState({ downloadBps: 0, uploadBps: 0 })
  const [history, setHistory] = useState([])
  const [conns,   setConns]   = useState([])
  const alerts = useDataStore(s => s.alerts)
  const storeAddAlert = useDataStore(s => s.addAlert)
  const storeDismiss = useDataStore(s => s.dismissAlert)
  const [tab,     setTab]     = useState('connections') // 'connections' | 'alerts'

  // Load snapshot + subscribe to live updates
  useEffect(() => {
    // Get cached data immediately so popover feels instant
    api?.trayGetSnapshot().then(snap => {
      if (!snap) return
      setSpeed(snap.speed)
      setHistory(snap.history || [])
      setConns(snap.conns  || [])
    }).catch(() => {})

    const unsubs = [
      api?.onSpeedUpdate(s => {
        setSpeed(s)
        setHistory(h => [...h, { downloadBps: s.downloadBps, uploadBps: s.uploadBps }].slice(-40))
      }),
      api?.onConnectionsUpdate(setConns),
      api?.onNewAlert(a => storeAddAlert(a)),
      api?.onAlertDismissed((id) => storeDismiss(id)),
    ]
    return () => unsubs.forEach(fn => fn?.())
  }, [])

  const dismissAlert = useCallback((id) => {
    storeDismiss(id)
    api?.dismissAlert(id)
  }, [storeDismiss])

  const openDashboard = useCallback(() => {
    api?.trayOpenDashboard()
  }, [])

  const activeAlerts  = alerts.filter(a => !a.dismissed)
  const warnConns     = conns.filter(c => c.riskLevel === 'warning' || c.riskLevel === 'critical')

  return (
    <div style={{
      width: 340, height: 480,
      display: 'flex', flexDirection: 'column',
      background: '#0d1220',
      fontFamily: "'IBM Plex Sans', sans-serif",
      color: '#dce8f5',
      userSelect: 'none',
      overflow: 'hidden',
      '--font-mono': "'IBM Plex Mono', monospace",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* ── HEADER ── */}
      <div style={{
        padding: '14px 14px 0',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        flexShrink: 0,
      }}>
        {/* Logo row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', fontFamily: "'IBM Plex Mono', monospace" }}>
              Netwoke
            </span>
            <div style={{
              width: 5, height: 5, borderRadius: '50%', background: '#00d4b8',
              boxShadow: '0 0 6px #00d4b8', animation: 'tray-pulse 2s ease-in-out infinite',
            }} />
          </div>
          <button onClick={openDashboard} style={{
            background: 'rgba(77,157,224,0.1)',
            border: '1px solid rgba(77,157,224,0.25)',
            borderRadius: 6, padding: '4px 10px',
            color: '#4d9de0', fontSize: 10, fontWeight: 500,
            cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif",
            transition: 'background 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(77,157,224,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(77,157,224,0.1)'}
          >
            Open Dashboard →
          </button>
        </div>

        {/* Speed row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12,
        }}>
          {[
            { label: '↓ Download', value: speed.downloadHuman, key: 'downloadBps', color: '#4d9de0' },
            { label: '↑ Upload',   value: speed.uploadHuman,   key: 'uploadBps',   color: '#3ecf8e' },
          ].map(({ label, value, key, color }) => (
            <div key={key} style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 8, padding: '8px 10px',
            }}>
              <div style={{ fontSize: 8, color: 'rgba(220,232,245,0.35)', fontFamily: "'IBM Plex Mono', monospace",
                textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>{label}</div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, fontWeight: 500, color, letterSpacing: '-0.01em', lineHeight: 1 }}>
                {value}
              </div>
              <MiniChart history={history} dataKey={key} color={color} />
            </div>
          ))}
        </div>

        {/* Stats strip
        <div style={{
          display: 'flex', gap: 12, marginBottom: 10,
          fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
        }}>
          <span style={{ color: 'rgba(220,232,245,0.4)' }}>
            <span style={{ color: '#4d9de0', fontWeight: 600 }}>{conns.length}</span> connections
          </span>
          {warnConns.length > 0 && (
            <span style={{ color: '#f5a623' }}>
              ⚠ {warnConns.length} warning{warnConns.length !== 1 ? 's' : ''}
            </span>
          )}
          {activeAlerts.length > 0 && (
            <span style={{ color: '#f5a623' }}>
               {activeAlerts.length} alert{activeAlerts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div> */}

        {/* Tabs */}
        <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.07)', marginLeft: -14, marginRight: -14 }}>
          <Tab label="Connections" active={tab === 'connections'} count={conns.length}
            onClick={() => setTab('connections')} />
          <Tab label="Alerts" active={tab === 'alerts'} count={activeAlerts.length}
            warn={activeAlerts.length > 0} onClick={() => setTab('alerts')} />
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'connections' && (
          conns.length === 0
            ? <Empty icon="🌐" text="No active connections" />
            : conns.slice(0, 20).map(c => <ConnRow key={c.id} conn={c} />)
        )}
        {tab === 'alerts' && (
          activeAlerts.length === 0
            ? <Empty icon="✅" text="No active alerts" />
            : activeAlerts.map(a => <AlertRow key={a.id} alert={a} onDismiss={dismissAlert} />)
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        padding: '8px 14px', flexShrink: 0,
        borderTop: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 9, color: 'rgba(220,232,245,0.2)', fontFamily: "'IBM Plex Mono', monospace" }}>
          Netwoke v1.0.2
        </span>
        <button onClick={openDashboard} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 9, color: 'rgba(220,232,245,0.3)',
          fontFamily: "'IBM Plex Sans', sans-serif",
          transition: 'color 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(220,232,245,0.7)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(220,232,245,0.3)'}
        >
          Full dashboard →
        </button>
      </div>

      <style>{`
        @keyframes tray-pulse {
          0%,100% { opacity:1; box-shadow: 0 0 6px #00d4b8; }
          50%      { opacity:.3; box-shadow: none; }
        }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
      `}</style>
    </div>
  )
}

function Empty({ icon, text }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 8,
      padding: '32px 0',
    }}>
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 12, color: 'rgba(220,232,245,0.35)' }}>{text}</div>
    </div>
  )
}
